import React, { useState, useEffect, useCallback } from 'react';
import { Clock, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

const STATUS_CONFIG = {
  on_track: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2, label: 'On Track' },
  warning: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: Clock, label: 'At Risk' },
  critical: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: AlertTriangle, label: 'Critical' },
  breached: { color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300', icon: XCircle, label: 'SLA Breached' },
  met: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2, label: 'SLA Met' },
  none: { color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-200', icon: Clock, label: 'No SLA' },
};

function formatCountdown(totalSeconds) {
  if (totalSeconds <= 0) return '00:00:00';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatDuration(minutes) {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function SlaTimer({ slaStatus }) {
  const [remaining, setRemaining] = useState(slaStatus?.remaining_seconds || 0);

  useEffect(() => {
    setRemaining(slaStatus?.remaining_seconds || 0);
  }, [slaStatus?.remaining_seconds]);

  // Live countdown
  useEffect(() => {
    if (!slaStatus || slaStatus.status === 'met' || slaStatus.status === 'none') return;
    const interval = setInterval(() => {
      setRemaining(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [slaStatus]);

  if (!slaStatus) return null;

  const config = STATUS_CONFIG[slaStatus.status] || STATUS_CONFIG.none;
  const Icon = config.icon;
  const progress = slaStatus.resolution_minutes
    ? Math.max(0, Math.min(100, (remaining / (slaStatus.resolution_minutes * 60)) * 100))
    : 0;

  return (
    <div className={`border ${config.border} ${config.bg} rounded-lg p-4`} data-testid="sla-timer">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${config.color}`} strokeWidth={2} />
          <span className={`text-xs font-bold uppercase tracking-wider ${config.color}`}>{config.label}</span>
        </div>
        {slaStatus.status !== 'met' && slaStatus.status !== 'none' && (
          <span className={`font-mono text-lg font-bold tracking-tight ${config.color}`} data-testid="sla-countdown">
            {formatCountdown(remaining)}
          </span>
        )}
      </div>

      {/* Progress bar */}
      {slaStatus.status !== 'met' && slaStatus.status !== 'none' && (
        <div className="h-1.5 bg-white/50 rounded-full mb-3 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-1000 ${
              slaStatus.status === 'breached' ? 'bg-red-500'
                : slaStatus.status === 'critical' ? 'bg-red-400'
                : slaStatus.status === 'warning' ? 'bg-amber-400'
                : 'bg-emerald-400'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Response SLA</p>
          <p className="text-xs font-semibold text-gray-700">
            {formatDuration(slaStatus.response_minutes || 0)}
            {slaStatus.response_breached && <span className="text-red-500 ml-1">(breached)</span>}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">Resolution SLA</p>
          <p className="text-xs font-semibold text-gray-700">
            {formatDuration(slaStatus.resolution_minutes || 0)}
            {slaStatus.breached && <span className="text-red-500 ml-1">(breached)</span>}
          </p>
        </div>
      </div>

      {slaStatus.resolution_deadline && slaStatus.status !== 'met' && (
        <p className="text-[10px] text-gray-400 mt-2">
          Deadline: {new Date(slaStatus.resolution_deadline).toLocaleString()}
        </p>
      )}
    </div>
  );
}

// Badge component for ticket lists
export function SlaBadge({ slaStatus }) {
  if (!slaStatus || slaStatus.status === 'none') return null;
  const config = STATUS_CONFIG[slaStatus.status] || STATUS_CONFIG.none;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${config.bg} ${config.color} border ${config.border}`} data-testid="sla-badge">
      {slaStatus.status === 'breached' ? 'SLA BREACH' : slaStatus.status === 'critical' ? 'SLA CRITICAL' : slaStatus.status === 'warning' ? 'SLA AT RISK' : slaStatus.status === 'met' ? 'SLA MET' : 'ON TRACK'}
    </span>
  );
}
